"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ComplexDropdown = void 0;
var classnames_1 = require("classnames");
var PropTypes = require("prop-types");
var React = require("react");
var react_transition_group_1 = require("react-transition-group");
var honeycomb_1 = require("../../honeycomb");
var Icon_1 = require("../Icon");
var Item_1 = require("./Item");
var ItemContent_1 = require("./ItemContent");
var utils_1 = require("./utils");
var ComplexDropdown = function (props) {
    var _a;
    var className = props.className, selectedOptionIndex = props.selectedOptionIndex, helptext = props.helptext, id = props.id, _b = props.isValid, isValid = _b === void 0 ? true : _b, errormessage = props.errormessage, label = props.label, onChange = props.onChange, onClick = props.onClick, placeholder = props.placeholder, options = props.options;
    var selectedItemIndex = React.useMemo(function () { return (0, utils_1.getSelectedOptionIndex)(selectedOptionIndex, placeholder, options.length); }, [selectedOptionIndex, options.length, placeholder]);
    var _c = React.useState(false), isOpen = _c[0], setIsOpen = _c[1];
    var _d = React.useState({ selectedItemIndex: selectedItemIndex, activeItemIndex: selectedItemIndex }), indexState = _d[0], setIndexState = _d[1];
    var wrapperRef = React.useRef(null);
    var comboboxRef = React.useRef(null);
    var itemsRef = React.useRef([]);
    var updateIndexState = function (update) {
        setIndexState(__assign(__assign({}, indexState), update));
    };
    React.useEffect(function () {
        var _a;
        (_a = itemsRef.current[indexState.activeItemIndex]) === null || _a === void 0 ? void 0 : _a.focus();
    }, [indexState.activeItemIndex]);
    React.useEffect(function () {
        itemsRef.current = itemsRef.current.slice(0, options.length);
    }, [options]);
    React.useEffect(function () {
        updateIndexState({ activeItemIndex: indexState.selectedItemIndex });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    React.useEffect(function () { return updateIndexState({ selectedItemIndex: (0, utils_1.getSelectedOptionIndex)(selectedOptionIndex, placeholder, options.length) }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , [selectedOptionIndex]);
    var cancel = function () {
        var _a;
        setIsOpen(false);
        (_a = comboboxRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var move = function (index) {
        updateIndexState({ activeItemIndex: index });
    };
    var actionedOnComponent = function (target) { var _a; return (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(target); };
    var onItemClick = function (index) {
        var _a;
        if (indexState.selectedItemIndex !== index && typeof onChange === 'function' && isOpen) {
            onChange(index, indexState.selectedItemIndex);
        }
        updateIndexState({ selectedItemIndex: index });
        setIsOpen(false);
        (_a = comboboxRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var mouseUpHandler = function (event) {
        var _a;
        if (!actionedOnComponent(event.target)) {
            setIsOpen(false);
            return;
        }
        if (event.button === 0) {
            var optionIndex = itemsRef.current.findIndex(function (option) { return option === null || option === void 0 ? void 0 : option.contains(event.target); });
            if (optionIndex > -1) {
                onItemClick(optionIndex);
                return;
            }
            if ((_a = comboboxRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) {
                onDropdownClick(event);
            }
        }
    };
    var keyDownHandler = function (event) {
        if (!actionedOnComponent(event.target)) {
            setIsOpen(false);
            return;
        }
        var index = indexState.activeItemIndex;
        var perhapsMove = function () {
            if (index !== indexState.activeItemIndex) {
                var min = 0;
                var max = options.length - 1;
                if (index < min) {
                    index = max;
                }
                else if (index > max) {
                    index = min;
                }
                // avoid scrolling while navigating with arrow keys
                event.preventDefault();
                move(index);
            }
        };
        var enterHandler = function () {
            if (event.target === comboboxRef.current) {
                onDropdownClick(event);
            }
            else {
                var optionIndex = itemsRef.current.findIndex(function (option) { return event.target === option; });
                onItemClick(optionIndex);
            }
        };
        var arrowDownHandler = function () { index++; perhapsMove(); };
        var arrowUpHandler = function () { index--; perhapsMove(); };
        var callback = {
            ' ': enterHandler,
            'ArrowDown': arrowDownHandler,
            'ArrowUp': arrowUpHandler,
            'Down': arrowDownHandler,
            'Enter': enterHandler,
            'Esc': cancel,
            'Escape': cancel,
            'Up': arrowUpHandler,
        }[event.key];
        callback === null || callback === void 0 ? void 0 : callback();
    };
    (0, utils_1.useEventListener)('keydown', keyDownHandler);
    (0, utils_1.useEventListener)('mouseup', mouseUpHandler);
    var onDropdownClick = function (event) {
        var _a;
        if (typeof onClick === 'function') {
            onClick(event);
            if (event.defaultPrevented) {
                return;
            }
        }
        setIsOpen(!isOpen);
        if (!isOpen) {
            (_a = itemsRef.current[indexState.selectedItemIndex]) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    return (React.createElement("div", { id: id + "-complexdropdown", className: (0, classnames_1.default)('cdd-container', 'form-group', (_a = {}, _a[className || ''] = className, _a)), ref: wrapperRef, "data-hcid": "c001-" + process.env.REACT_APP_VERSION },
        label && React.createElement("label", { id: id + "-complexdropdown-label" }, label),
        React.createElement("div", { tabIndex: 0, role: "combobox", "aria-haspopup": "listbox", "aria-expanded": isOpen ? 'true' : 'false', "aria-controls": isOpen ? id + "-complexdropdown-listbox" : undefined, "aria-labelledby": id + "-complexdropdown-label", "aria-describedby": id + "-complexdropdown-" + (isValid ? 'helptext' : 'errormessage'), className: (0, classnames_1.default)('cdd-combobox', { 'cdd-combobox--invalid': !isValid }), ref: comboboxRef },
            indexState.selectedItemIndex >= 0 ?
                React.createElement(ItemContent_1.default, { image: options[indexState.selectedItemIndex].image, textLeftPrimary: options[indexState.selectedItemIndex].textLeftPrimary, textLeftSecondary: options[indexState.selectedItemIndex].textLeftSecondary, textRightSecondary: options[indexState.selectedItemIndex].textRightSecondary, textRightPrimary: options[indexState.selectedItemIndex].textRightPrimary })
                : React.createElement("div", { className: 'cdd-placeholder cdd-placeholder--line24' }, placeholder),
            React.createElement(Icon_1.Icon, { className: (0, classnames_1.default)('cdd-combobox__chevron', { 'cdd-combobox__chevron--open': isOpen }), symbol: "controlAccordionOpen24" })),
        React.createElement(react_transition_group_1.CSSTransition, { in: isOpen, appear: isOpen, timeout: 200, mountOnEnter: true, unmountOnExit: true, classNames: {
                enter: 'cdd-listbox-enter',
                enterActive: 'cdd-listbox-enter--active',
                exit: 'cdd-listbox-exit',
                exitActive: 'cdd-listbox-exit--active',
            } },
            React.createElement("div", { className: 'cdd-listbox', tabIndex: -1, role: "listbox", id: id + "-complexdropdown-listbox", "aria-labelledby": id + "-complexdropdown-label" }, options.map(function (option, index) { return (React.createElement(Item_1.default, { tabIndex: indexState.activeItemIndex === index ? 0 : -1, key: id + "-complexdropdown-item-" + index, 
                // tslint:disable-next-line: jsx-no-lambda
                setRef: function (el) { return itemsRef.current[index] = el; }, 
                // tslint:disable-next-line: jsx-no-lambda
                position: index + 1, setSize: options.length, selected: indexState.selectedItemIndex === index },
                React.createElement(ItemContent_1.default, __assign({}, option)))); }))),
        isValid && helptext && (React.createElement(honeycomb_1.HelpText, { id: id + "-complexdropdown" }, helptext)),
        !isValid && errormessage && (React.createElement(honeycomb_1.ErrorMessage, { id: id + "-complexdropdown", message: errormessage, forceVisible: true }))));
};
exports.ComplexDropdown = ComplexDropdown;
exports.ComplexDropdown.propTypes = {
    className: PropTypes.string,
    errormessage: PropTypes.string,
    helptext: PropTypes.string,
    id: PropTypes.string.isRequired,
    isValid: PropTypes.bool,
    label: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.shape(ItemContent_1.ItemContentPropTypes)).isRequired,
    placeholder: PropTypes.string,
    selectedOptionIndex: PropTypes.number,
};
exports.ComplexDropdown.displayName = 'ComplexDropdown';
